import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { Layout } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { forceReloadProject } from '@hooks/API/Project/UseProject';
import { forceReloadProjectAccountingDetails } from '@hooks/API/Project/UseProjectAccountingDetails';
import { forceReloadCreditNote, useCreditNote } from '@hooks/API/UseCreditNotes';
import { forceReloadSearchInvoices } from '@hooks/API/UseInvoices';
import { forceReloadInvoice } from '@hooks/API/UseInvoices';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import { Loading } from '@components/Gui/Loading';
import { NwLinkButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import NwDrawer from '@components/Gui/NwDrawer';
import NwResult from '@components/Gui/NwResult';
import EditTextsDrawer from '@components/InvoicesAndCreditNotes/Common/EditTextsDrawer';
import SentStatus from '@components/InvoicesAndCreditNotes/Common/SentStatus';
import CreateCreditNoteTransactionDrawer from '@components/InvoicesAndCreditNotes/CreditNotePage/Drawers/CreateCreditNoteTransactionDrawer';

import CreditNoteClientInfo from './CreditNoteClientInfo';
import CreditNotePageHeader from './CreditNotePageHeader';
import CreditNotePageRows from './CreditNotePageRows';
import CreditNotePageSider from './CreditNotePageSider';
import CreditNotePageTop from './CreditNotePageTop';
import CreditNotePageTotals from './CreditNotePageTotals';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const StyledLayout = styled(Layout)`
    min-height: ${(props) => (props.$inDrawer ? 'calc(100vh - 240px)' : 'calc(100vh - 220px)')};
    padding: ${(props) => (props.$inDrawer ? '0' : '8px')};
    background: white;
    margin: 0 0.5rem;
    display: flex;
    flex-direction: row;
`;

const CreditNoteContent = styled.div`
    //width: 100%;
    padding: 1rem 2rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    flex: 1 1 0;
`;

const CreditNotePage = ({ creditNoteId, inDrawer, onClose }) => {
    const navigate = useNavigate();
    const { data: creditNoteData, error } = useCreditNote(creditNoteId);
    const configurations = useConfigurations();

    const [showEditDrawer, setShowEditDrawer] = useState(false);
    const [isLocked, setIsLocked] = useState(false);

    const [originalInvoiceData, setOriginalInvoiceData] = useState(null);
    const [createTransaction, setCreateTransaction] = useState(null);
    const accounts = [];
    const schemas = [];
    if (configurations && configurations.COA) {
        const COAjs = JSON.parse(configurations.COA);
        if (COAjs.accounts && COAjs.accounts.length > 0) {
            accounts.push(...COAjs.accounts);
        }
        if (COAjs.schemas && COAjs.schemas.length > 0) {
            schemas.push(...COAjs.schemas);
        }
    }

    const handleCreateTransaction = (transactionID, statementID, transactionFamily) => {
        setCreateTransaction({ transactionID, statementID, transactionFamily });
    };

    useEffect(() => {
        if (creditNoteData) {
            setIsLocked(creditNoteData.Posted || creditNoteData.ReadyToPost);
            const invoiceID = creditNoteData.SourceInvoiceID;
            Axios.get(`/Invoices/${invoiceID}`).then((res) => {
                setOriginalInvoiceData(res);
            });
        }
    }, [creditNoteData]);

    const handleTransactionCreatedOrUpdated = (ID) => {
        forceReloadCreditNote(creditNoteData.ID);
        forceReloadProjectAccountingDetails(ID);
        forceReloadProject(ID);
        setCreateTransaction(null);
    };

    const handleDeleteCreditNote = () => {
        if (inDrawer) {
            onClose();
        } else {
            forceReloadSearchInvoices();
            navigate(`/accounting-invoices`);
        }
    };

    const handleCloseTextsDrawer = () => {
        forceReloadInvoice(creditNoteData.ID);
        setShowEditDrawer(false);
    };

    // if (error) {
    //     return (
    //         <NwResult
    //             status="error"
    //             title="This credit note has been deleted"
    //         />
    //     )
    // }

    return creditNoteData ? (
        <div>
            <CreditNotePageHeader
                creditNoteData={creditNoteData}
                inDrawer={inDrawer}
                handleShowEditDrawer={() => setShowEditDrawer(true)}
                onDelete={handleDeleteCreditNote}
                locked={isLocked}
                originalInvoiceData={originalInvoiceData}
            />
            <StyledLayout $inDrawer={inDrawer}>
                {originalInvoiceData ? (
                    <CreditNotePageSider
                        invoiceData={originalInvoiceData}
                        creditNoteData={creditNoteData}
                        onShowEditDrawer={() => setShowEditDrawer(true)}
                        locked={isLocked}
                        onCreateTransaction={handleCreateTransaction}
                    />
                ) : (
                    <Loading />
                )}
                <CreditNoteContent>
                    {(creditNoteData.ReadyToPost || creditNoteData.Posted) && <SentStatus data={creditNoteData} />}
                    <CreditNotePageTop creditNoteData={creditNoteData} locked={isLocked} />
                    <CreditNoteClientInfo creditNoteData={creditNoteData} />
                    <NwDivider>CREDIT NOTE DESCRIPTION</NwDivider>
                    <div>
                        <p>{creditNoteData.HeaderNote}</p>
                        <NwLinkButton
                            //disabled={isLocked}
                            onClick={() => setShowEditDrawer(true)}
                            icon={byPrefixAndName.fasl['message-lines']}
                            label={`${creditNoteData.HeaderNote ? 'edit' : 'add'} credit note description`}
                        />
                    </div>
                    <NwDivider>CREDIT NOTE LINES</NwDivider>
                    <CreditNotePageRows
                        creditNoteData={creditNoteData}
                        onTransactionsUpdated={handleTransactionCreatedOrUpdated}
                        locked={isLocked}
                    />
                    <CreditNotePageTotals creditNoteData={creditNoteData} />
                    <NwDivider>CREDIT NOTE FOOTER</NwDivider>
                    <div>
                        <p>{creditNoteData.FooterNote}</p>
                        <NwLinkButton
                            //disabled={isLocked}
                            onClick={() => setShowEditDrawer(true)}
                            icon={byPrefixAndName.fasl['message-lines']}
                            label={`${creditNoteData.FooterNote ? 'edit' : 'add'} terms additional text`}
                        />
                    </div>
                </CreditNoteContent>
            </StyledLayout>
            {showEditDrawer && <EditTextsDrawer onClose={handleCloseTextsDrawer} data={creditNoteData} />}
            {createTransaction && (
                <NwDrawer
                    title="Create Credit Note Adjustment"
                    open={true}
                    width={800}
                    onClose={() => setCreateTransaction(null)}
                >
                    <CreateCreditNoteTransactionDrawer
                        creditNoteID={creditNoteData.ID}
                        creditNoteType={creditNoteData.TypeName}
                        creditNoteCurrency={creditNoteData.Currency}
                        transactionID={createTransaction.transactionID}
                        statementType={creditNoteData.TypeName.replace('Credit', '').toLowerCase()}
                        statementID={createTransaction.statementID}
                        transactionFamily={createTransaction.transactionFamily}
                        onTransactionCreated={handleTransactionCreatedOrUpdated}
                    />
                </NwDrawer>
            )}
        </div>
    ) : (
        <Loading />
    );
};

export default CreditNotePage;
